import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTooltipHeader } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import {
  STAY_DATE_GRANULARITIES,
  type StayDateGranularity,
} from 'lumen/utils/constants/net-revenue-constants';
import { calculateRelativeChange } from 'lumen/utils/net-revenue/metrics';
import type { StayDatePoint } from 'lumen/utils/net-revenue/types';
import { deltaColorClass } from 'lumen/utils/net-revenue/variance';

import styles from './tooltip.module.css';

interface MetricDifference {
  colorClass: string;
  formatted: string;
}

interface HotelNetRevenueStayDateChartTooltipSignature {
  Args: {
    comparisonLabel: string | null;
    currencyCode: string;
    granularity: StayDateGranularity;
    point: StayDatePoint;
  };
}

export default class HotelNetRevenueStayDateChartTooltip extends Component<HotelNetRevenueStayDateChartTooltipSignature> {
  @service declare intl: Services['intl'];

  get avgCostDifference(): MetricDifference {
    // Avg cost inverts colours — a higher cost is "bad" (critical).
    return this.#buildMetricDifference(
      this.args.point.avgCost,
      this.args.point.comparison?.avgCost,
      true,
      'percent',
    );
  }

  get costDifference(): MetricDifference {
    // Cost inverts colours — a higher cost is "bad" (critical), matching the
    // cost-drilldown table.
    return this.#buildAbsoluteDifference(
      this.args.point.cost,
      this.args.point.comparison?.cost,
      true,
      'currency',
    );
  }

  // Percentage-point difference (not a relative change) — cost % going from
  // 24.0% to 24.3% reads as "+0.3%", not the ~1.3% relative change.
  get costPercentDifference(): MetricDifference {
    return this.#buildAbsoluteDifference(
      this.args.point.costPercent,
      this.args.point.comparison?.costPercent,
      true,
      'percent',
    );
  }

  get formattedAvgCost(): string {
    return this.#formatCurrency(this.args.point.avgCost, 2);
  }

  get formattedComparisonCost(): string {
    return this.#formatCurrency(this.args.point.comparison?.cost);
  }

  get formattedComparisonNetRevenue(): string {
    return this.#formatCurrency(this.args.point.comparison?.netRevenue);
  }

  get formattedComparisonOccupancy(): string {
    const value = this.args.point.comparison?.occupancy;
    return value === undefined
      ? this.intl.t('general.missing')
      : this.#formatPercent(value);
  }

  get formattedComparisonRoomRevenue(): string {
    return this.#formatCurrency(this.args.point.comparison?.roomRevenue);
  }

  get formattedCost(): string {
    return this.#formatCurrency(this.args.point.cost);
  }

  get formattedCostPercent(): string {
    return this.#formatPercent(this.args.point.costPercent);
  }

  get formattedNetRevenue(): string {
    return this.#formatCurrency(this.args.point.netRevenue);
  }

  get formattedOccupancy(): string {
    return this.#formatPercent(this.args.point.occupancy);
  }

  get formattedRoomRevenue(): string {
    return this.#formatCurrency(this.args.point.roomRevenue);
  }

  get formattedTitle(): string {
    const date = dayjs(this.args.point.label);
    if (this.args.granularity === STAY_DATE_GRANULARITIES.WEEK) {
      return this.intl.t('general.week_of', { date: date.format('D MMM') });
    }
    if (this.args.granularity === STAY_DATE_GRANULARITIES.MONTH) {
      return date.format('MMM YYYY');
    }
    return date.format('dddd DD MMMM YYYY');
  }

  get netRevenueDifference(): MetricDifference {
    return this.#buildAbsoluteDifference(
      this.args.point.netRevenue,
      this.args.point.comparison?.netRevenue,
      false,
      'currency',
    );
  }

  // Percentage-point difference (not a relative change) — occupancy going
  // from 31.6% to 39.7% reads as "+8.1%", not the ~25.6% relative change.
  get occupancyDifference(): MetricDifference {
    return this.#buildAbsoluteDifference(
      this.args.point.occupancy,
      this.args.point.comparison?.occupancy,
      false,
      'percent',
    );
  }

  get roomRevenueDifference(): MetricDifference {
    return this.#buildAbsoluteDifference(
      this.args.point.roomRevenue,
      this.args.point.comparison?.roomRevenue,
      false,
      'currency',
    );
  }

  get showComparison(): boolean {
    return this.args.comparisonLabel !== null;
  }

  /**
   * Absolute difference (current − comparison), as opposed to a relative
   * change — e.g. cost % going from 24.0% to 24.3% reads as a "+0.3%"
   * percentage-point difference, and net revenue going from $5,359 to $6,131
   * reads as "+$772", not a relative percent. Returns "—" / neutral when
   * there is no comparable value.
   */
  #buildAbsoluteDifference(
    current: number,
    comparison: number | undefined,
    invertColors: boolean,
    style: 'currency' | 'percent',
  ): MetricDifference {
    return this.#formatDifference(
      comparison === undefined ? null : current - comparison,
      invertColors,
      style,
    );
  }

  /**
   * Relative change of a metric vs its comparison, formatted as a signed
   * percent with a colour class. `invertColors` flips success/critical for
   * "lower is better" metrics (cost). Returns "—" / neutral when there is no
   * comparable value.
   */
  #buildMetricDifference(
    current: number,
    comparison: number | undefined,
    invertColors: boolean,
    style: 'currency' | 'percent',
  ): MetricDifference {
    return this.#formatDifference(
      calculateRelativeChange(current, comparison),
      invertColors,
      style,
    );
  }

  #formatCurrency(
    value: number | undefined,
    maximumFractionDigits = 0,
  ): string {
    if (value === undefined) {
      return this.intl.t('general.missing');
    }
    return this.intl.formatNumber(value, {
      style: 'currency',
      currency: this.args.currencyCode,
      currencyDisplay: 'symbol',
      maximumFractionDigits,
      minimumFractionDigits: 0,
    });
  }

  #formatDifference(
    change: number | null,
    invertColors: boolean,
    style: 'currency' | 'percent',
  ): MetricDifference {
    if (change === null) {
      return {
        formatted: this.intl.t('general.missing'),
        colorClass: 'text-neutral-default',
      };
    }
    return {
      formatted:
        style === 'currency'
          ? this.intl.formatNumber(change, {
              style: 'currency',
              currency: this.args.currencyCode,
              currencyDisplay: 'symbol',
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
              signDisplay: 'exceptZero',
            })
          : this.intl.formatNumber(change, {
              style: 'percent',
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
              signDisplay: 'exceptZero',
            }),
      // Threshold matches each style's display rounding, so noise that
      // rounds to zero stays neutral: 0.5 currency units at 0 decimals,
      // 0.0005 (0.05%) at 1 decimal percent.
      colorClass: deltaColorClass(change, {
        invert: invertColors,
        neutralThreshold: style === 'currency' ? 0.5 : 0.0005,
      }),
    };
  }

  #formatPercent(value: number): string {
    return this.intl.formatNumber(value, {
      style: 'percent',
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    });
  }

  <template>
    <div class={{styles.tooltip}} data-test-id="stay-date-chart-tooltip">
      <PrismTooltipHeader data-test-id="stay-date-chart-tooltip-title">
        {{this.formattedTitle}}
      </PrismTooltipHeader>
      <div class={{styles.kpi-row}}>
        <div
          class={{styles.kpi-tile}}
          data-test-id="stay-date-chart-tooltip-kpi-cost-percent"
        >
          <div class={{styles.kpi-value-row}}>
            <span
              class="text-400--bold {{styles.kpi-value}}"
              data-test-id="stay-date-chart-tooltip-kpi-cost-percent-value"
            >
              {{this.formattedCostPercent}}
            </span>
            {{#if this.showComparison}}
              <span
                class="{{styles.kpi-delta}}
                  {{this.costPercentDifference.colorClass}}"
                data-test-id="stay-date-chart-tooltip-kpi-cost-percent-delta"
              >
                {{this.costPercentDifference.formatted}}
              </span>
            {{/if}}
          </div>
          <div class={{styles.kpi-caption}}>{{t
              "net_revenue.tooltip_cost_percent_of_room_revenue"
            }}</div>
        </div>
        <div
          class={{styles.kpi-tile}}
          data-test-id="stay-date-chart-tooltip-kpi-avg-cost"
        >
          <div class={{styles.kpi-value-row}}>
            <span
              class="text-400--bold {{styles.kpi-value}}"
              data-test-id="stay-date-chart-tooltip-kpi-avg-cost-value"
            >
              {{this.formattedAvgCost}}
            </span>
            {{#if this.showComparison}}
              <span
                class="{{styles.kpi-delta}}
                  {{this.avgCostDifference.colorClass}}"
                data-test-id="stay-date-chart-tooltip-kpi-avg-cost-delta"
              >
                {{this.avgCostDifference.formatted}}
              </span>
            {{/if}}
          </div>
          <div class={{styles.kpi-caption}}>{{t
              "net_revenue.tooltip_avg_cost_per_room_night"
            }}</div>
        </div>
      </div>
      <table class={{styles.table}}>
        <thead>
          <tr>
            <th
              class={{styles.header-secondary}}
              data-test-id="stay-date-chart-tooltip-property-header"
            >
              {{t "net_revenue.tooltip_property"}}
            </th>
            <th class={{styles.header}}>
              {{t "general.current_period"}}
            </th>
            {{#if this.showComparison}}
              <th
                class={{styles.header-secondary}}
                data-test-id="stay-date-chart-tooltip-comparison-header"
              >
                {{@comparisonLabel}}
              </th>
              <th
                class={{styles.header-secondary}}
                data-test-id="stay-date-chart-tooltip-difference-header"
              >
                {{t "general.difference"}}
              </th>
            {{/if}}
          </tr>
        </thead>
        <tbody>
          <tr data-test-id="stay-date-chart-tooltip-row-net-revenue">
            <th class={{styles.label}}>{{t "net_revenue.net_revenue"}}</th>
            <td class={{styles.value}}>{{this.formattedNetRevenue}}</td>
            {{#if this.showComparison}}
              <td class={{styles.value-secondary}}>
                {{this.formattedComparisonNetRevenue}}
              </td>
              <td
                class="{{styles.value-secondary}}
                  {{this.netRevenueDifference.colorClass}}"
                data-test-id="stay-date-chart-tooltip-difference-net-revenue"
              >
                {{this.netRevenueDifference.formatted}}
              </td>
            {{/if}}
          </tr>
          <tr data-test-id="stay-date-chart-tooltip-row-cost">
            <th class={{styles.label}}>{{t "net_revenue.cost"}}</th>
            <td class={{styles.value}}>{{this.formattedCost}}</td>
            {{#if this.showComparison}}
              <td class={{styles.value-secondary}}>
                {{this.formattedComparisonCost}}
              </td>
              <td
                class="{{styles.value-secondary}}
                  {{this.costDifference.colorClass}}"
                data-test-id="stay-date-chart-tooltip-difference-cost"
              >
                {{this.costDifference.formatted}}
              </td>
            {{/if}}
          </tr>
          <tr data-test-id="stay-date-chart-tooltip-row-room-revenue">
            <th class={{styles.label}}>{{t "net_revenue.room_revenue"}}</th>
            <td class={{styles.value}}>{{this.formattedRoomRevenue}}</td>
            {{#if this.showComparison}}
              <td class={{styles.value-secondary}}>
                {{this.formattedComparisonRoomRevenue}}
              </td>
              <td
                class="{{styles.value-secondary}}
                  {{this.roomRevenueDifference.colorClass}}"
                data-test-id="stay-date-chart-tooltip-difference-room-revenue"
              >
                {{this.roomRevenueDifference.formatted}}
              </td>
            {{/if}}
          </tr>
          <tr data-test-id="stay-date-chart-tooltip-row-occupancy">
            <th class={{styles.label}}>{{t "general.occupancy"}}</th>
            <td class={{styles.value}}>{{this.formattedOccupancy}}</td>
            {{#if this.showComparison}}
              <td class={{styles.value-secondary}}>
                {{this.formattedComparisonOccupancy}}
              </td>
              <td
                class="{{styles.value-secondary}}
                  {{this.occupancyDifference.colorClass}}"
                data-test-id="stay-date-chart-tooltip-difference-occupancy"
              >
                {{this.occupancyDifference.formatted}}
              </td>
            {{/if}}
          </tr>
        </tbody>
      </table>
    </div>
  </template>
}
